import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, User, Pill, FileText, Package, Wallet, BarChart3,
  LogOut, Menu, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { C, RADIUS, SHADOW } from '../styles/theme';
import logo from '../assets/startup/static-logo.png';

// Grouped the way a vendor actually works: what they sell, what has been asked
// of them, and what they have earned. One flat list of eight made the money
// rows sit level with the catalogue rows, which is not how a vendor thinks.
const SECTIONS = [
  {
    title: 'Overview',
    tint: C.indigo,
    tintLight: C.indigoLight,
    items: [
      { label: 'Home', Icon: Home, path: '/vendor' },
      { label: 'Profile', Icon: User, path: '/vendor/profile' },
    ],
  },
  {
    title: 'Catalogue',
    tint: C.purple,
    tintLight: C.purpleLight,
    items: [
      { label: 'Products', Icon: Pill, path: '/vendor/products' },
    ],
  },
  {
    title: 'Order flow',
    tint: C.blue,
    tintLight: C.blueLight,
    items: [
      { label: 'Quotes', Icon: FileText, path: '/vendor/quotes' },
      { label: 'Orders', Icon: Package, path: '/vendor/orders' },
    ],
  },
  {
    title: 'Money',
    tint: C.primary,
    tintLight: C.primaryLight,
    items: [
      { label: 'Account', Icon: Wallet, path: '/vendor/account' },
      { label: 'Reports', Icon: BarChart3, path: '/vendor/reports' },
    ],
  },
];

// Same row treatment as AdminNav: a coloured rail on the active item, and the
// icon tile taking the section tint only when active — so exactly one row in
// the drawer carries colour at a time.
const NavRow = ({ item, active, tint, tintLight }) => {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={item.path}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        padding: '10px 12px 10px 16px',
        marginBottom: 3,
        textDecoration: 'none',
        background: active || hover ? C.surfaceSunk : 'transparent',
        border: 'none',
        borderRadius: RADIUS.md,
        color: active ? C.ink : C.inkSoft,
        fontSize: 14.5,
        fontWeight: active ? 700 : 500,
        letterSpacing: '-0.01em',
        transition: 'background 0.12s ease, color 0.12s ease',
      }}
    >
      {active && (
        <span style={{
          position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
          width: 4, height: 22, borderRadius: '0 4px 4px 0', background: tint,
        }} />
      )}
      <span style={{
        width: 32, height: 32, borderRadius: RADIUS.sm, flexShrink: 0,
        display: 'grid', placeItems: 'center',
        background: active ? tintLight : C.surfaceSunk,
        transition: 'background 0.12s ease',
      }}>
        <item.Icon size={17} strokeWidth={2} color={active ? tint : C.inkSoft} />
      </span>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.label}
      </span>
    </Link>
  );
};

const VendorNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, profile } = useAuth();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/vendor') return location.pathname === '/vendor';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  return (
    <>
      {/* Trigger */}
      <button
        onClick={() => setOpen((p) => !p)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        style={{
          position: 'fixed', top: 14, left: 14, zIndex: 300,
          width: 44, height: 44, borderRadius: RADIUS.md,
          background: open ? C.primary : C.surface,
          border: `1px solid ${open ? C.primary : C.borderStrong}`,
          color: open ? '#ffffff' : C.ink,
          boxShadow: open ? SHADOW.pop : SHADOW.card,
          cursor: 'pointer', lineHeight: 1,
          transition: 'background 0.15s ease, border-color 0.15s ease',
        }}
      >
        {open
          ? <X size={19} strokeWidth={2.2} style={{ display: 'block', margin: '0 auto' }} />
          : <Menu size={19} strokeWidth={2.2} style={{ display: 'block', margin: '0 auto' }} />}
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.28)', zIndex: 200 }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: 'fixed', left: 0, top: 0, bottom: 0, width: 288,
          background: C.surface,
          borderRight: `1px solid ${C.border}`,
          zIndex: 250,
          display: 'flex', flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: open ? SHADOW.pop : 'none',
        }}
      >
        {/* Brand. Left padding clears the fixed trigger button. */}
        <div style={{
          padding: '17px 16px 17px 70px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        }}>
          <img
            src={logo}
            alt="Pharvlink"
            style={{ width: 38, height: 38, objectFit: 'contain', display: 'block', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, color: C.ink, fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>
              Pharv<span style={{ color: C.primary }}>link</span>
            </p>
            <p style={{
              margin: '2px 0 0', color: C.inkFaint, fontSize: 11,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              Vendor{profile?.facility_name ? ` · ${profile.facility_name}` : ''}
            </p>
          </div>
        </div>

        {/* Sections */}
        <div style={{ padding: '16px 13px 20px', flex: 1, overflowY: 'auto' }}>
          {SECTIONS.map((section, idx) => (
            <div key={section.title} style={{ marginBottom: idx === SECTIONS.length - 1 ? 6 : 22 }}>
              <div style={{ padding: '0 12px', marginBottom: 9, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 10, height: 2.5, borderRadius: 2, background: section.tint, flexShrink: 0 }} />
                <p style={{
                  margin: 0, fontSize: 11.5, fontWeight: 800, color: C.inkSoft,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>
                  {section.title}
                </p>
              </div>
              {section.items.map((item) => (
                <NavRow
                  key={item.path}
                  item={item}
                  tint={section.tint}
                  tintLight={section.tintLight}
                  active={isActive(item.path)}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Sign out */}
        <div style={{ padding: 13, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: 12, borderRadius: RADIUS.md,
              border: `1px solid ${C.border}`, background: C.surface,
              color: C.inkSoft, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
          >
            <LogOut size={16} strokeWidth={2} color={C.inkSoft} />
            <span>Sign out</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default VendorNav;